'use client';

import { Fieldset } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { CalendarEvent } from '@/app/(marketing-site)/bezplatna-konsultacja/_components/CalendarEvent';
import { useTRPC } from '@/app/api/trpc/config';
import type { AdvancedMentoringProductDTO, BasicMentoringProductDTO } from '@/backend/modules/payment/endpoints/mutations/stripe-checkout/input.dto/input.dto';
import { ORDER_TYPE } from '@/backend/modules/payment/endpoints/mutations/stripe-checkout/types';
import { Link } from '@/frontend/design-system/actions/Link';
import { showErrorToastWithContactFormLink } from '@/frontend/design-system/feedback/Toaster/ToasterError';
import { Checkbox, CheckboxField } from '@/frontend/design-system/forms/Checkbox';
import { ErrorMessage, Field, Label } from '@/frontend/design-system/forms/Fieldset';
import { Input } from '@/frontend/design-system/forms/Input';
import { Textarea } from '@/frontend/design-system/forms/Textarea';
import { Caption } from '@/frontend/design-system/typography/Caption';
import { BuyData } from '../../BuyData';
import { AdvancedMentoringInfo } from './_components/AdvancedMentoringInfo';
import { BasicMentoringInfo } from './_components/BasicMentoringInfo';
import { type CallFormSchema, callFormSchemaZ } from './schema';
type CallFormProps = {
  children?: React.ReactNode;
  mentoringProduct: BasicMentoringProductDTO | AdvancedMentoringProductDTO;
  onClose: () => void;
  selectedEvent: CalendarEvent | null;
};
export function CallForm({
  children,
  mentoringProduct,
  onClose,
  selectedEvent
}: CallFormProps) {
  const methods = useForm<CallFormSchema>({
    defaultValues: {
      fullname: '',
      phoneNumber: '',
      message: '',
      acceptTerms: false
    },
    mode: 'onSubmit',
    resolver: zodResolver(callFormSchemaZ)
  });
  const trpc = useTRPC();
  const bookAPhoneCallMutation = useMutation(trpc.phoneCallBooking.bookAPhoneCall.mutationOptions());
  const onSubmit = async (data: CallFormSchema) => {
    try {
      await bookAPhoneCallMutation.mutateAsync({
        scheduleId: selectedEvent?.id ?? '',
        client: {
          phoneNumber: data.phoneNumber,
          message: data.message,
          fullname: data.fullname
        },
        mentoringDetails: {
          subscription: mentoringProduct.subscription,
          advancedMentoringDetails: mentoringProduct.order === ORDER_TYPE.ADVANCED_MENTORING ? {
            hoursWithMentor: mentoringProduct.hoursWithMentor,
            mentorReplyTime: mentoringProduct.mentorReplyTime,
            linesOfCode: mentoringProduct.linesOfCode,
            applicationHelp: mentoringProduct.applicationHelp,
            careerCoaching: mentoringProduct.careerCoaching
          } : undefined
        }
      });
      // await sendSMSToAlgoMutation.mutateAsync({
      //   message: getAlgoSMSContent(mentoringProduct, selectedEvent!, {
      //     fullname: data.fullname,
      //     phoneNumber: data.phoneNumber,
      //     message: data.message
      //   })
      // })

      // await sendSMSToClientMutation.mutateAsync({
      //   phoneNumber: data.phoneNumber,
      //   message: getClientSMSContent(mentoringProduct, selectedEvent!)
      // })

      onClose();
      toast.success('Udało się!', {
        description: 'Umówiłeś się na spotkanie z mentorem'
      });
    } catch {
      showErrorToastWithContactFormLink();
      return;
    }
  };
  return <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <section className='flex flex-col gap-6 md:flex-row'>
          <section className='flex-1'>
            <Fieldset>
              <Field>
                <Label>Imię i nazwisko*</Label>
                <Input {...methods.register('fullname')} name='fullname' type='text' className='w-full' />

                <ErrorMessage>{methods.formState.errors.fullname?.message}</ErrorMessage>
              </Field>

              <Field>
                <Label>Numer telefonu*</Label>
                <Input {...methods.register('phoneNumber')} name='phoneNumber' type='text' />

                <ErrorMessage>{methods.formState.errors.phoneNumber?.message}</ErrorMessage>
              </Field>

              <Field>
                <Label>Wiadomość do mentora</Label>
                <Textarea {...methods.register('message')} resizable={false} name='message' className='h-40' />

                <ErrorMessage>{methods.formState.errors.message?.message}</ErrorMessage>
              </Field>

              <Field>
                <CheckboxField>
                  <Checkbox {...methods.register('acceptTerms')} onChange={e => methods.setValue('acceptTerms', e)} />

                  <Label>
                    Oświadczam, że akceptuję{' '}
                    <Link href={'/regulamin'} className='font-semibold text-algoprimary'>
                      regulamin
                    </Link>
                    *
                  </Label>
                </CheckboxField>

                <ErrorMessage>{methods.formState.errors.acceptTerms?.message}</ErrorMessage>
              </Field>

              <Caption className='py-1'>* Pola wymagane</Caption>
            </Fieldset>
          </section>

          <section className='flex-1'>
            {mentoringProduct.order === ORDER_TYPE.BASIC_MENTORING ? <BasicMentoringInfo activeSubscriptionTab={mentoringProduct.subscription} /> : <AdvancedMentoringInfo mentoringProduct={mentoringProduct} />}

            <BuyData mentoringProduct={mentoringProduct} />
          </section>
        </section>
        {children}
        {/* <div className='flex flex-row gap-6 justify-self-end'>
          <Button marketing marketingVariant='outline' onClick={onClose}>
            Anuluj
          </Button>
          <Button marketing type='submit' disabled={!selectedEvent}>
            Umów spotkanie
          </Button>
         </div> */}
      </form>
    </FormProvider>;
}